import { useConfigControllerGetPublicTenantConfig } from 'korint-sdk';
import { useKorintConfig } from 'korint-sdk/react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../lib';
import { Textarea, type TextareaProps } from '../forms/textarea/textarea';
import { isSpeechToTextSupported } from './feature-detect';
import { MicButton } from './MicButton';
import type { SpeechToTextProviderName } from './providers/speech-to-text-provider';
import { useDictation } from './useDictation';
import { Group, Stack } from '@folio/components/ui/flex';

export interface TextareaSpeechToTextConfig {
  /** Optional explicit provider override; otherwise derived from the tenant config. */
  provider?: SpeechToTextProviderName;
}

export interface TextareaWithMicProps extends Omit<TextareaProps, 'value' | 'onChange'> {
  value: string;
  onValueChange: (next: string) => void;
  /**
   * Per-field SPEECH_TO_TEXT opt-in. The mic shows only when this is set AND the tenant
   * config has an `speechToText` provider AND the browser supports capture.
   */
  speechToText?: TextareaSpeechToTextConfig;
}

export const TextareaWithMic = React.forwardRef<HTMLTextAreaElement, TextareaWithMicProps>(
  ({ value, onValueChange, speechToText, ...rest }, ref) => {
    const { clientOptions } = useKorintConfig();
    const fieldOptedIn = !!speechToText;

    const { data: tenantConfig } = useConfigControllerGetPublicTenantConfig({
      query: { enabled: fieldOptedIn && isSpeechToTextSupported() },
    });
    const tenantProvider = (
      tenantConfig as { speechToText?: { provider?: SpeechToTextProviderName } } | undefined
    )?.speechToText?.provider;
    const provider = speechToText?.provider ?? tenantProvider;

    const enabled =
      fieldOptedIn &&
      !!clientOptions.baseUrl &&
      !!clientOptions.tenant &&
      !!provider &&
      isSpeechToTextSupported();

    if (!enabled || !provider) {
      return (
        <Textarea
          ref={ref}
          value={value}
          onChange={e => onValueChange(e.currentTarget.value)}
          {...rest}
        />
      );
    }

    return <TextareaWithMicInner ref={ref} value={value} onValueChange={onValueChange} {...rest} />;
  },
);
TextareaWithMic.displayName = 'TextareaWithMic';

const TextareaWithMicInner = React.forwardRef<HTMLTextAreaElement, TextareaWithMicProps>(
  ({ value, onValueChange, speechToText: _speechToText, ...rest }, ref) => {
    const { t, i18n } = useTranslation();
    const preferredLanguage = i18n.language?.startsWith('fr') ? 'fr-FR' : 'en-US';

    const valueRef = React.useRef(value);
    valueRef.current = value;

    const handleFinal = React.useCallback(
      (segment: string) => {
        const cur = valueRef.current;
        onValueChange(cur ? `${cur} ${segment}`.trim() : segment);
      },
      [onValueChange],
    );

    const dictation = useDictation({
      onFinalSegment: handleFinal,
      preferredLanguage,
    });

    const display = dictation.partial ? `${value}${value ? ' ' : ''}${dictation.partial}` : value;
    const liveMessage =
      dictation.state === 'recording'
        ? t('speechToText.listening', 'Listening…')
        : dictation.error
          ? t(`speechToText.${dictation.error}`, dictation.error)
          : '';

    const recording = dictation.state === 'recording';

    return (
      <Stack gap={1}>
        <div className="relative">
          <Textarea
            ref={ref}
            value={display}
            onChange={e => onValueChange(e.currentTarget.value)}
            readOnly={recording}
            className={cn('pr-12', recording && 'ring-1 ring-red-300', rest.className)}
            {...rest}
          />
          {/* Mic floats at the top-right corner of the textarea. */}
          <div className="absolute right-2 top-2">
            <MicButton state={dictation.state} onStart={dictation.start} onStop={dictation.stop} />
          </div>
        </div>
        {liveMessage && (
          <Group
            aria-live="polite"
            className={cn('gap-1.5 text-xs', recording ? 'text-red-500' : 'text-muted-foreground')}
          >
            {recording && (
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-red-500" />
              </span>
            )}
            {liveMessage}
          </Group>
        )}
      </Stack>
    );
  },
);
TextareaWithMicInner.displayName = 'TextareaWithMicInner';
